if (!customElements.get('pfk-family-meal-addons')) {
  customElements.define(
    'pfk-family-meal-addons',
    class PFKFamilyMealAddons extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.onClickBound = this.onClick.bind(this);
        this.onPointerOverBound = this.onPointerOver.bind(this);
        this.onPointerOutBound = this.onPointerOut.bind(this);
        this.onFocusInBound = this.onFocusIn.bind(this);
        this.onFocusOutBound = this.onFocusOut.bind(this);
        this.onContextMenuBound = this.onContextMenu.bind(this);
        this.onDocumentKeydownBound = this.onDocumentKeydown.bind(this);
        this.onResizeBound = () => this.closeAllPreviews();

        this.addEventListener('click', this.onClickBound);
        this.addEventListener('pointerover', this.onPointerOverBound);
        this.addEventListener('pointerout', this.onPointerOutBound);
        this.addEventListener('focusin', this.onFocusInBound);
        this.addEventListener('focusout', this.onFocusOutBound);
        this.addEventListener('contextmenu', this.onContextMenuBound);
        document.addEventListener('keydown', this.onDocumentKeydownBound);
        window.addEventListener('resize', this.onResizeBound);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickBound);
        this.removeEventListener('pointerover', this.onPointerOverBound);
        this.removeEventListener('pointerout', this.onPointerOutBound);
        this.removeEventListener('focusin', this.onFocusInBound);
        this.removeEventListener('focusout', this.onFocusOutBound);
        this.removeEventListener('contextmenu', this.onContextMenuBound);
        document.removeEventListener('keydown', this.onDocumentKeydownBound);
        window.removeEventListener('resize', this.onResizeBound);
        delete this.dataset.initialized;
      }

      onClick(event) {
        const addonToggle = event.target.closest('[data-addon-toggle]');
        if (addonToggle && this.contains(addonToggle)) {
          this.toggleAddonVisibility(addonToggle);
          return;
        }

        const variantButton = event.target.closest('[data-addon-variant]');
        if (variantButton && this.contains(variantButton)) {
          this.selectVariant(variantButton);
          return;
        }

        const quantityButton = event.target.closest('[data-addon-minus], [data-addon-plus]');
        if (!quantityButton || !this.contains(quantityButton)) return;

        const row = quantityButton.closest('[data-addon-row]');
        if (!row) return;

        const delta = quantityButton.hasAttribute('data-addon-plus') ? 1 : -1;
        this.changeQuantity(row, delta);
      }

      toggleAddonVisibility(button) {
        const shouldExpand = button.getAttribute('aria-expanded') !== 'true';

        this.closeAllPreviews();
        this.querySelectorAll('[data-addon-collapsible]').forEach((row) => {
          row.hidden = !shouldExpand;
        });

        button.setAttribute('aria-expanded', String(shouldExpand));

        const label = button.querySelector('[data-addon-toggle-label]');
        const nextLabel = shouldExpand ? button.dataset.collapseLabel : button.dataset.expandLabel;
        if (label && nextLabel) label.textContent = nextLabel;
      }

      onPointerOver(event) {
        if (!this.supportsHoverPreview()) return;

        const trigger = event.target.closest('[data-addon-preview-trigger]');
        if (
          !trigger ||
          !this.contains(trigger) ||
          (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget))
        )
          return;
        this.openPreview(trigger);
      }

      onPointerOut(event) {
        if (!this.supportsHoverPreview()) return;

        const trigger = event.target.closest('[data-addon-preview-trigger]');
        if (
          !trigger ||
          !this.contains(trigger) ||
          (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget))
        )
          return;
        this.closePreview(trigger.closest('[data-addon-row]'));
      }

      onFocusIn(event) {
        if (!this.supportsHoverPreview()) return;

        const trigger = event.target.closest('[data-addon-preview-trigger]');
        if (trigger && this.contains(trigger)) this.openPreview(trigger);
      }

      onFocusOut(event) {
        if (!this.supportsHoverPreview()) return;

        const trigger = event.target.closest('[data-addon-preview-trigger]');
        if (
          !trigger ||
          !this.contains(trigger) ||
          (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget))
        )
          return;
        this.closePreview(trigger.closest('[data-addon-row]'));
      }

      onContextMenu(event) {
        if (this.supportsHoverPreview()) return;

        const trigger = event.target.closest('[data-addon-preview-trigger]');
        if (trigger && this.contains(trigger)) event.preventDefault();
      }

      onDocumentKeydown(event) {
        if (event.key === 'Escape') this.closeAllPreviews();
      }

      supportsHoverPreview() {
        return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      }

      openPreview(trigger) {
        const row = trigger.closest('[data-addon-row]');
        const preview = row?.querySelector('[data-addon-image-preview]');
        if (!row || !preview) return;

        this.closeAllPreviews(row);
        preview.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        this.positionPreview(trigger, preview);
      }

      positionPreview(trigger, preview) {
        const margin = 12;
        const gap = 12;
        const triggerRect = trigger.getBoundingClientRect();
        const previewRect = preview.getBoundingClientRect();

        let left = triggerRect.right + gap;
        if (left + previewRect.width > window.innerWidth - margin) {
          left = triggerRect.left - previewRect.width - gap;
        }
        left = Math.max(margin, Math.min(left, window.innerWidth - previewRect.width - margin));

        let top = triggerRect.top + (triggerRect.height - previewRect.height) / 2;
        top = Math.max(margin, Math.min(top, window.innerHeight - previewRect.height - margin));

        preview.style.left = `${Math.round(left)}px`;
        preview.style.top = `${Math.round(top)}px`;
      }

      closePreview(row) {
        if (!row) return;

        const preview = row.querySelector('[data-addon-image-preview]');
        const trigger = row.querySelector('[data-addon-preview-trigger]');
        if (preview) {
          preview.hidden = true;
          preview.style.removeProperty('left');
          preview.style.removeProperty('top');
        }
        trigger?.setAttribute('aria-expanded', 'false');
      }

      closeAllPreviews(exceptRow = null) {
        this.querySelectorAll('[data-addon-row]').forEach((row) => {
          if (row !== exceptRow) this.closePreview(row);
        });
      }

      selectVariant(button) {
        if (button.disabled) return;

        const row = button.closest('[data-addon-row]');
        if (!row) return;

        row.dataset.variantId = button.dataset.variantId || '';
        row.dataset.variantAvailable = 'true';

        row.querySelectorAll('[data-addon-variant]').forEach((variantButton) => {
          const isSelected = variantButton === button;
          variantButton.classList.toggle('is-selected', isSelected);
          variantButton.setAttribute('aria-pressed', String(isSelected));
        });

        const price = row.querySelector('[data-addon-price]');
        if (price && button.dataset.variantPrice) price.textContent = button.dataset.variantPrice;

        const image = row.querySelector('[data-addon-image]');
        if (image && button.dataset.variantImage) image.src = button.dataset.variantImage;

        const previewImage = row.querySelector('[data-addon-preview-image]');
        const previewSource = button.dataset.variantPreviewImage || button.dataset.variantImage;
        if (previewImage && previewSource) previewImage.src = previewSource;

        const plusButton = row.querySelector('[data-addon-plus]');
        if (plusButton) plusButton.disabled = false;

        this.notifyChange();
      }

      changeQuantity(row, delta) {
        if (delta > 0 && row.dataset.variantAvailable !== 'true') return;

        const quantityOutput = row.querySelector('[data-addon-quantity]');
        if (!quantityOutput) return;

        const currentQuantity = Number.parseInt(quantityOutput.value || quantityOutput.textContent || '0', 10) || 0;
        const nextQuantity = Math.max(0, currentQuantity + delta);

        quantityOutput.value = String(nextQuantity);
        quantityOutput.textContent = String(nextQuantity);

        const minusButton = row.querySelector('[data-addon-minus]');
        if (minusButton) minusButton.disabled = nextQuantity === 0;

        this.notifyChange();
      }

      getCartItems() {
        return Array.from(this.querySelectorAll('[data-addon-row]'))
          .map((row) => {
            const quantityOutput = row.querySelector('[data-addon-quantity]');
            const quantity = Number.parseInt(quantityOutput?.value || quantityOutput?.textContent || '0', 10) || 0;
            const id = Number.parseInt(row.dataset.variantId || '', 10);

            if (!id || quantity <= 0 || row.dataset.variantAvailable !== 'true') return null;
            return { id, quantity };
          })
          .filter(Boolean);
      }

      notifyChange() {
        this.dispatchEvent(new CustomEvent('pfk:family-addons:change', { bubbles: true }));
      }
    }
  );
}
